import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import secureStorage from "./config/encrypt";
import { Provider } from "react-redux";
import store from "./store/store";
import reportWebVitals from "./reportWebVitals";
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client/core";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";

import { Buffer } from "buffer";
import process from "process";

// Translation JSON imports
import common_id from "./translations/id/common.json";
import common_en from "./translations/en/common.json";
import more_id from "./translations/id/more.json";
import more_en from "./translations/en/more.json";
import rule_engine_id from "./translations/id/rule-engine.json";
import rule_engine_en from "./translations/en/rule-engine.json";
import login_en from "./translations/en/login.json";
import login_id from "./translations/id/login.json";
import loan_details_en from "./translations/en/loan-details.json";
import loan_details_id from "./translations/id/loan-details.json";
import loan_list_en from "./translations/en/loan-list.json";
import loan_list_id from "./translations/id/loan-list.json";
import common_mmv_en from "./translations/en/common-mmv.json";
import common_mmv_id from "./translations/id/common-mmv.json";
import loanexpected_en from "./translations/en/loanexpected.json";
import loanexpected_id from "./translations/id/loanexpected.json";

import common_id_hrf from "./translations/housing-refinance/id/common.json";
import common_en_hrf from "./translations/housing-refinance/en/common.json";
import more_id_hrf from "./translations/housing-refinance/id/more.json";
import more_en_hrf from "./translations/housing-refinance/en/more.json";
import rule_engine_id_hrf from "./translations/housing-refinance/id/rule-engine.json";
import rule_engine_en_hrf from "./translations/housing-refinance/en/rule-engine.json";
import login_en_hrf from "./translations/housing-refinance/en/login.json";
import login_id_hrf from "./translations/housing-refinance/id/login.json";
import loan_details_en_hrf from "./translations/housing-refinance/en/loan-details.json";
import loan_details_id_hrf from "./translations/housing-refinance/id/loan-details.json";
import loan_list_en_hrf from "./translations/housing-refinance/en/loan-list.json";
import loan_list_id_hrf from "./translations/housing-refinance/id/loan-list.json";
import common_mmv_en_hrf from "./translations/housing-refinance/en/common-mmv.json";
import common_mmv_id_hrf from "./translations/housing-refinance/id/common-mmv.json";
import loanexpected_en_hrf from "./translations/housing-refinance/en/loanexpected.json";
import loanexpected_id_hrf from "./translations/housing-refinance/id/loanexpected.json";
import AllTranslation from './translations/refinance/translation';
import hierarchyEn from "./translations/refinance/en/hierarchy.json";
import hierarchyId from "./translations/refinance/id/hierarchy.json";
import { updateAuthUser } from './store/action/allAction';
import { API_URL,IS_EMPOWER_LOGIN, IS_FAVOURITE_LOGIN, IS_TRIIVCO_LOGIN } from "./config/constant";

// Node.js polyfills for browser
window.Buffer = Buffer;
window.process = process;

// Determine tab name
const TABNAME = IS_FAVOURITE_LOGIN() ? "FAV" : IS_TRIIVCO_LOGIN() ? "TRIV" : IS_EMPOWER_LOGIN() ? "EMPOWER" : "OTO";
const selectedHierarchyEn = hierarchyEn[TABNAME]; 
const selectedHierarchyId = hierarchyId[TABNAME]; 

const getEnTranslationJson = (file) => {
  return (AllTranslation[`${file}_${process.env.REACT_APP_CONFIG_FILE_NAME}`])
    ? Object.assign({}, AllTranslation[file], AllTranslation[`${file}_${process.env.REACT_APP_CONFIG_FILE_NAME}`])
    : AllTranslation[file];
}

// Initialize i18next
i18next.init({
  interpolation: { escapeValue: false },
  lng: secureStorage.getItem("lang") === "id" ? "id" : "en",
  resources: {
    en: {
      common: common_en,
      more: more_en,
      rule_engine: rule_engine_en,
      login: login_en,
      loan_details: loan_details_en,
      loan_list: loan_list_en,
      common_mmv: common_mmv_en,
      loanexpected: loanexpected_en,
      common_hrf: common_en_hrf,
      more_hrf: more_en_hrf,
      rule_engine_hrf: rule_engine_en_hrf,
      login_hrf: login_en_hrf,
      loan_details_hrf: loan_details_en_hrf,
      loan_list_hrf: loan_list_en_hrf,
      common_mmv_hrf: common_mmv_en_hrf,
      loanexpected_hrf: loanexpected_en_hrf,
      inventory: getEnTranslationJson('inventory_en'),
      dealers: getEnTranslationJson('dealer_en'),
      lead: getEnTranslationJson('lead_en'),
      ac_manager: getEnTranslationJson('ac_manager_en'),
      hierarchy: selectedHierarchyEn
    },
    id: {
      common: common_id,
      more: more_id,
      rule_engine: rule_engine_id,
      login: login_id,
      loan_details: loan_details_id,
      loan_list: loan_list_id,
      common_mmv: common_mmv_id,
      loanexpected: loanexpected_id,
      inventory: AllTranslation.inventory_id,
      dealers: AllTranslation.dealer_id,
      lead: AllTranslation.lead_id,
      ac_manager: AllTranslation.ac_manager_id,
      hierarchy: selectedHierarchyId,
      common_hrf: common_id_hrf,
      more_hrf: more_id_hrf,
      rule_engine_hrf: rule_engine_id_hrf,
      login_hrf: login_id_hrf,
      loan_details_hrf: loan_details_id_hrf,
      loan_list_hrf: loan_list_id_hrf,
      common_mmv_hrf: common_mmv_id_hrf,
      loanexpected_hrf: loanexpected_id_hrf,
    }
  }
});

let loginInfo = secureStorage.getItem("loginUserInfo");
let authToken = secureStorage.getItem("authToken");
if(authToken && loginInfo?.data?.user_data) {
  store.dispatch(updateAuthUser({ user_data: loginInfo.data.user_data || {}, token: authToken }));
}

// Apollo Client setup
const httpLink = createHttpLink({
  uri: `${API_URL.GETWAY_API}dif-revamp/api/v1`,
  fetch: (uri, options) => {
    const timeout = 30000;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Request timeout')), timeout);
      fetch(uri, options)
        .then(response => { clearTimeout(timer); resolve(response); })
        .catch(err => { clearTimeout(timer); reject(err); });
    });
  },
});

const authLink = setContext((_, { headers }) => {
  const loginInfo = secureStorage.getItem('loginUserInfo') || {};
  const token = loginInfo?.data?.token || null;

  return {
    headers: {
      ...headers,
      api_source: 'loanbox',
      ...(token ? { Authorization: token } : {})
    }
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'ignore' },
    query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
  },
});

// Render
const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <I18nextProvider i18n={i18next}>
    <Provider store={store}>
      <ApolloProvider client={client}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ApolloProvider>
    </Provider>
  </I18nextProvider>
);

reportWebVitals();
